import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { PageHeader } from "@/components/ui-kit";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { KeyRound, Download, Loader2, RefreshCw, MessageCircle, Trash2 } from "lucide-react";

const shareText = (a) => `Dear ${a.name}, your SPARKORA EDU parent login:\nUsername: ${a.username}\nPassword: ${a.starting_password}\nOpen ${window.location.origin} and sign in. You will be asked to set your own password.`;

function AccountRow({ a, onReset }) {
  return (
    <tr data-testid={`parent-acc-${a.id}`} className="border-t border-slate-100 align-top">
      <td className="py-2 pr-3 font-semibold text-slate-800">{a.name}</td>
      <td className="py-2 pr-3 font-mono">{a.username}</td>
      <td className="py-2 pr-3 font-mono" data-testid={`parent-pw-${a.id}`}>{a.starting_password || <span className="text-xs font-sans text-emerald-700">Changed by parent</span>}{a.just_reset && <span className="ml-2 text-[10px] font-sans font-semibold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">New</span>}</td>
      <td className="py-2 pr-3 text-xs text-slate-600 max-w-xs">{a.children.join(", ") || "—"}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {a.starting_password && (
          <a data-testid={`parent-wa-${a.id}`} href={`https://wa.me/91${a.username}?text=${encodeURIComponent(shareText(a))}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center text-xs text-emerald-700 hover:underline mr-3"><MessageCircle size={13} className="mr-1" />WhatsApp</a>
        )}
        <button data-testid={`parent-reset-${a.id}`} onClick={() => onReset(a)} className="inline-flex items-center text-xs text-violet-700 hover:underline"><RefreshCw size={13} className="mr-1" />Reset</button>
      </td>
    </tr>
  );
}

export default function ParentAccountsPage() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("all");
  const [mode, setMode] = useState("random");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const bparam = branchId === "all" ? undefined : branchId;
  const load = () => api.get("/parent-accounts", { params: { branch_id: bparam } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    if (!window.confirm(`Create parent logins for every student with a mobile number${bparam ? " in this branch" : ""}? Logins whose parent hasn't signed in yet also get the chosen starting password; parents who already set their own password are not changed.`)) return;
    setBusy(true);
    try {
      const r = (await api.post("/parent-accounts/bulk", { branch_id: bparam, mode })).data;
      toast.success(`${r.created} new logins, ${r.updated} existing logins given the new starting password, ${r.students_linked} students linked${r.kept ? `, ${r.kept} kept (parent already set own password)` : ""}${r.students_without_mobile ? `, ${r.students_without_mobile} students have no mobile number` : ""}`, { duration: 9000 });
      if (r.no_dob) toast.warning(`${r.no_dob} parent(s) got a random password because their child has no date of birth saved.`, { duration: 9000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function deleteAll() {
    const typed = window.prompt(`This deletes ALL ${rows.length} parent logins${bparam ? " in this branch" : ""}. Students stay, but parents can no longer sign in.\n\nType DELETE to confirm.`);
    if (typed !== "DELETE") return;
    setBusy(true);
    try {
      const r = await api.post("/parent-accounts/delete-all", { branch_id: bparam });
      toast.success(`${r.data.deleted} parent logins deleted`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function resetAll() {
    if (!window.confirm(`Reset the password of ALL ${rows.length} parent logins${bparam ? " in this branch" : ""}? Old passwords stop working and parents are signed out.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/parent-accounts/reset-all", { branch_id: bparam, mode });
      toast.success(`${r.data.reset} passwords reset`);
      if (r.data.no_dob) toast.warning(`${r.data.no_dob} parent(s) got a random password because their child has no date of birth saved. Add it on the student profile, then reset again.`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function reset(a) {
    if (!window.confirm(`Give ${a.name} a new starting password?`)) return;
    try {
      const r = await api.post(`/parent-accounts/${a.id}/reset`, { mode });
      setRows((xs) => xs.map((x) => (x.id === a.id ? { ...x, starting_password: r.data.starting_password, must_change_password: true, just_reset: true } : x)));
      toast.success(`Old password removed. New password for ${a.name}: ${r.data.starting_password}`);
    }
    catch (e) { toast.error(formatApiError(e)); }
  }
  function download() {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Parent", "Username", "Starting password", "Children"], ...rows.map((a) => [a.name, a.username, a.starting_password || "Changed by parent", a.children.join("; ")])];
    const el = document.createElement("a");
    el.href = URL.createObjectURL(new Blob([lines.map((l) => l.map(esc).join(",")).join("\n")], { type: "text/csv" }));
    el.download = "parent-logins.csv"; el.click();
  }
  const shown = rows.filter((a) => !q || `${a.name} ${a.username} ${a.children.join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppShell>
      <PageHeader eyebrow="Parents" title="Parent Logins" subtitle="Create a login for every family from the imported mobile numbers, then send each parent their username and password." />
      <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 flex flex-wrap items-end gap-3">
        <div><div className="text-xs text-slate-500 mb-1">Branch</div>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="pa-branch" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><div className="text-xs text-slate-500 mb-1">Starting password</div>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="pa-mode" className="w-72"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="random">Random for each parent</SelectItem><SelectItem value="mobile6">Last 6 digits of mobile</SelectItem><SelectItem value="dob">Child's date of birth (DDMMYYYY)</SelectItem></SelectContent>
          </Select></div>
        <Button data-testid="pa-create" className="btn-brand" disabled={busy} onClick={create}>{busy ? <Loader2 size={15} className="animate-spin mr-1" /> : <KeyRound size={15} className="mr-1" />} Create parent logins</Button>
        <span className="flex-1" />
        <Button data-testid="pa-delete-all" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" disabled={!rows.length || busy} onClick={deleteAll}><Trash2 size={15} className="mr-1" /> Delete all</Button>
        <Button data-testid="pa-reset-all" variant="outline" disabled={!rows.length || busy} onClick={resetAll}><RefreshCw size={15} className="mr-1" /> Reset all</Button>
        <Button data-testid="pa-download" variant="outline" disabled={!rows.length} onClick={download}><Download size={15} className="mr-1" /> Download list</Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Username = parent's 10-digit mobile number. Siblings with the same mobile share one login. Parents must set their own password at first sign-in; after that only "Changed by parent" is shown.</p>
      <Input data-testid="pa-search" placeholder="Search parent, mobile or child" value={q} onChange={(e) => setQ(e.target.value)} className="w-72 bg-white mb-3" />
      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto p-4">
        {shown.length === 0 ? <p className="text-sm text-slate-500">No parent logins yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3">Parent</th><th className="pr-3">Username</th><th className="pr-3">Starting password</th><th className="pr-3">Children</th><th /></tr></thead>
            <tbody>{shown.map((a) => <AccountRow key={a.id} a={a} onReset={reset} />)}</tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
